/**
 * Where brands and cameras live in the URL space.
 *
 * Brands sit at the root (`/dahua`) and their models one level below
 * (`/dahua/ipc-hfw1230s-s5`). Both used to hang off a prefix (`/brand/...`,
 * `/camera/<brand>-<model>`); `public/.htaccess` 301s every old address here.
 *
 * Kept in its own module because `brandSlug.ts` and `cameraSlug.ts` both need
 * these rules and already import one another.
 */

/** Lowercase, dash-separated, no leading or trailing dash. */
export function slugSegment(value: string): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Brand display name to URL segment, e.g. "Digital Watchdog" to "digital-watchdog". */
export function generateBrandSlug(manufacturer: string): string {
  return slugSegment(manufacturer);
}

/** Model name to URL segment. The brand is the segment above, never repeated here. */
export function generateModelSlug(model: string): string {
  return slugSegment(model);
}

/**
 * Segment that separates a hub's later pages from its models, so `/dahua/page/2`
 * can never be read as a model named "2". Verified against the catalog: no model
 * slugs to "page" (scripts/check-catalog-routes.mjs).
 */
export const BRAND_PAGE_SEGMENT = 'page';

/**
 * Root paths a brand may not take, because a real page already answers there.
 *
 * Two sources: every top-level route in `src/routes.tsx`, and every locale
 * prefix, since `/it` is Italian before it is a brand. No catalog brand
 * currently collides; `scripts/check-catalog-routes.mjs` fails the build if a
 * new route or brand ever changes that.
 */
export const RESERVED_TOP_LEVEL: ReadonlySet<string> = new Set([
  // Locale prefixes.
  'ar', 'bg', 'cs', 'da', 'de', 'el', 'es', 'et', 'fi', 'fil', 'fr', 'hi', 'hr',
  'hu', 'id', 'it', 'ja', 'ko', 'lt', 'lv', 'nl', 'pl', 'pt-br', 'pt-pt', 'ro',
  'sk', 'sl', 'sv', 'th', 'tr', 'uk', 'vi', 'en',
  // Application and marketing routes.
  '404', 'about', 'accept-invite', 'access-control-design', 'api-docs',
  'attributions', 'auth', 'axis-site-designer-alternative', 'bandwidth-calculator',
  'bank-cctv-design-software', 'blog', 'brand', 'calculator', 'calculators',
  'camera', 'camera-physics', 'camera-placement-tool', 'cameras', 'cat6-max-length', 'cctv-design',
  'cctv-design-software', 'cctv-design-tool', 'cctv-layout', 'cctv-layout-software',
  'cctv-mapper', 'cctv-planning-tool', 'cctv-quotation-template',
  'cctv-with-pir-motion-detection', 'cctvdesigntool-alternative', 'changelog',
  'checkout-success', 'compare', 'compliance', 'dahua-design-tool-alternative',
  'dashboard', 'demo', 'docs', 'en-62676-4-calculator', 'equipment', 'examples', 'faq',
  'features', 'feedback', 'flir-raven-alternative', 'free', 'gdpr-cctv', 'glossary',
  'hikvision-hitools-alternative', 'hospital-cctv-design-software',
  'hotel-cctv-design-software', 'how-it-works', 'i-pro-sdt-alternative',
  'intrusion-detection-design', 'ipvm-alternative', 'ipvm-alternative-for-banks',
  'ipvm-alternative-for-hospitals', 'ipvm-alternative-for-parking',
  'ipvm-alternative-for-retail', 'ipvm-alternative-for-schools',
  'ipvm-alternative-for-warehouses', 'johnson-criteria-calculator',
  'jvsg-alternative', 'jvsg-alternative-for-banks', 'jvsg-alternative-for-hospitals',
  'jvsg-alternative-for-parking-lots', 'jvsg-alternative-for-retail',
  'jvsg-alternative-for-schools', 'jvsg-alternative-for-warehouses', 'jvsg-pricing',
  'jvsg-review', 'jvsg-vs-cctvplanner', 'lens', 'lens-calculator',
  'money-back-guarantee', 'ndaa-cctv-compliance', 'network-planning-for-security',
  'nvr', 'nvr-vs-dvr', 'onvif', 'onvif-device-manager', 'optex-design-tool', 'organization', 'p', 'partners',
  'en-50131', 'perimeter-intrusion-design', 'poe-switch-for-ip-cameras', 'pricing', 'resources', 'rodo', 'rtsp', 'rtsp-url',
  'security-camera-design-tool', 'security-camera-planner', 'security-compliance',
  'settings', 'siteowl-alternative', 'sso', 'storage-calculator', 'switch-from-jvsg', 't568a-vs-t568b',
  'system-surveyor-alternative', 'tenders', 'terms',
  'testimonials', 'try', 'unsubscribed', 'use-cases', 'verify', 'videocad-alternative',
  'wildfire-detection-camera-planning',
  'lpr-camera-planning',
  // Reserved ahead of use so a future route can never displace a live brand.
  'admin', 'account', 'api', 'assets', 'catalog', 'help', 'login', 'logout',
  'pages', 'search', 'signup', 'sitemap', 'static', 'support',
]);

export function isReservedTopLevel(slug: string): boolean {
  return RESERVED_TOP_LEVEL.has(slug);
}

/**
 * A brand whose slug is reserved keeps the old prefixed address, so a name
 * clash costs that one brand its short URL instead of breaking a site route.
 */
export function brandHubPath(brandSlug: string): string {
  return isReservedTopLevel(brandSlug) ? `/brand/${brandSlug}` : `/${brandSlug}`;
}

/** Page 1 is the hub itself; later pages hang off BRAND_PAGE_SEGMENT. */
export function brandPagePath(brandSlug: string, page = 1): string {
  const hub = brandHubPath(brandSlug);
  return page <= 1 ? hub : `${hub}/${BRAND_PAGE_SEGMENT}/${page}`;
}

/** `/dahua/ipc-hfw1230s-s5`, or the legacy flat address for a reserved brand. */
export function cameraPath(brandSlug: string, modelSlug: string, legacySlug: string): string {
  return isReservedTopLevel(brandSlug) ? `/camera/${legacySlug}` : `/${brandSlug}/${modelSlug}`;
}

/** Same path from the raw names, for callers holding generated rows rather than CameraPageData. */
export function cameraPathFor(manufacturer: string, model: string, legacySlug: string): string {
  return cameraPath(generateBrandSlug(manufacturer), generateModelSlug(model), legacySlug);
}
